import { useEffect } from "react";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LeadProvider, useLead } from "@/context/LeadContext";
import { AuthProvider } from "@/lib/auth";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FloatingActions } from "@/components/FloatingActions";
import { ChatWidget } from "@/components/ChatWidget";
import { PageTranslator } from "@/components/PageTranslator";
import AuthCallback from "@/pages/AuthCallback";
import UploadPortal from "@/pages/UploadPortal";
import { LeadDialog } from "@/components/LeadDialog";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Businesses from "@/pages/Businesses";
import SegmentPage from "@/pages/SegmentPage";
import Franchise from "@/pages/Franchise";
import Investors from "@/pages/Investors";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Dashboard from "@/pages/Dashboard";
import Mall from "@/pages/Mall";
import Opportunity from "@/pages/Opportunity";
import RanksRewards from "@/pages/RanksRewards";
import HowItWorks from "@/pages/HowItWorks";
import RuralConnect from "@/pages/RuralConnect";
import { RouteSeo } from "@/components/Seo";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RefCapture() {
  const [params] = useSearchParams();
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem("ld_ref", ref);
  }, [params]);
  return null;
}

function PopupTrigger() {
  const { openLead } = useLead();
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/dashboard") || pathname === "/login" || pathname === "/register") return;
    if (sessionStorage.getItem("ld_popup_shown")) return;
    const timer = setTimeout(() => {
      const p = window.location.pathname;
      // Only auto-open the loan enquiry on finance-related pages to avoid off-brand / click-blocking on general Genesis pages
      const financePage = p.startsWith("/services") || p === "/businesses/finance";
      if (!financePage) return;
      sessionStorage.setItem("ld_popup_shown", "1");
      openLead({ source: "auto-popup" });
    }, 20000);
    return () => clearTimeout(timer);
  }, []);
  return null;
}

// Public site chrome (navbar/footer/floating) — hidden on dashboard & auth pages
function SiteShell({ children }) {
  const { pathname } = useLocation();
  const bare = pathname.startsWith("/dashboard") || pathname === "/login" || pathname === "/register" || pathname.startsWith("/u/");
  if (bare) return <>{children}</>;
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <FloatingActions />
      <ChatWidget />
      <LeadDialog />
      <PageTranslator />
    </>
  );
}

function AppBody() {
  const location = useLocation();
  // Google OAuth returns to {origin}/dashboard#session_id=... — intercept before routing
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <SiteShell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/businesses" element={<Businesses />} />
        <Route path="/businesses/:slug" element={<SegmentPage />} />
        <Route path="/mall" element={<Mall />} />
        <Route path="/opportunity" element={<Opportunity />} />
        <Route path="/affiliate" element={<Opportunity />} />
        <Route path="/ranks-rewards" element={<RanksRewards />} />
        <Route path="/rural-connect" element={<RuralConnect />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/franchise" element={<Franchise />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/u/:token" element={<UploadPortal />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="*" element={<Home />} />
      </Routes>
    </SiteShell>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
          <LeadProvider>
            <RouteSeo />
            <ScrollToTop />
            <RefCapture />
            <PopupTrigger />
            <AppBody />
            <Toaster position="top-center" richColors offset="80px" />
          </LeadProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
